import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { from, Observable, EMPTY, first } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService } from '@shared/services/notifications/notification.service';
import {
  Auth,
  EmailAuthProvider,
  TotpMultiFactorGenerator,
  multiFactor,
  reauthenticateWithCredential,
  UserCredential,
  TotpSecret,
  MultiFactorResolver,
  MultiFactorInfo,
  MultiFactorError,
  getMultiFactorResolver,
  AuthCredential,
  MultiFactorSession,
  MultiFactorAssertion,
  MultiFactorUser,
} from '@angular/fire/auth';

import { QrEnableComponent } from '../components/qr-enable/qr-enable.component';
import { ReAuthenticationComponent } from '../components/re-authentication/re-authentication';
import { ConfigurationComponent } from '../components/configuration/configuration.component';
import { AlreadyActivatedComponent } from '../components/already-activated/already-activated.component';
import { DeactivateComponent } from '../components/deactivate/deactivate.component';
import { EmailVerificationService } from '../../email-verification/service/email-verification.service';

@Injectable({
  providedIn: 'root',
})
export class TwoFactorAuthenticationService {
  private mfaResolver: MultiFactorResolver | null = null;
  private emailVerificationService: EmailVerificationService = inject(EmailVerificationService);
  dialogStyles: DynamicDialogConfig = {
    width: '385px',
    maskStyleClass: 'blur-backdrop',
    modal: true,
    closable: true,
    styleClass: 'twofa-dialog',
  };

  constructor(
    private dialogService: DialogService,
    private auth: Auth,
    private injector: Injector,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  openTwoFactorConfigurationDialog() {
    this.dialogService.open(ConfigurationComponent, this.dialogStyles);
  }

  openAlreadyActivatedDialog() {
    this.dialogService.open(AlreadyActivatedComponent, this.dialogStyles);
  }

  openDeactivateDialog() {
    this.dialogService.open(DeactivateComponent, this.dialogStyles);
  }

  openMfaDialog() {
    if (this.isMfaEnabled()) {
      this.openAlreadyActivatedDialog();
      return;
    }

    this.emailVerificationService
      .checkEmailVerified()
      .pipe(first())
      .subscribe((verified: boolean) => {
        if (!verified) {
          this.emailVerificationService.openVerificationDialog({
            titleKey: 'emailVerification.mfa.title',
            messageKey: 'emailVerification.mfa.message',
          });
          return;
        }
        this.openTwoFactorConfigurationDialog();
      });
  }

  isMfaEnabled(): boolean {
    return runInInjectionContext(
      this.injector,
      () => multiFactor(this.auth.currentUser!).enrolledFactors.length > 0
    );
  }

  openReAuthenticationDialog(type: 'enroll' | 'unenroll') {
    this.dialogService.open(ReAuthenticationComponent, {
      ...this.dialogStyles,
      data: {
        type,
      },
    });
  }

  openQrEnableDialog() {
    this.dialogService.open(QrEnableComponent, this.dialogStyles);
  }

  /**
   * Reautentica al usuario actual utilizando credenciales de correo y contraseña.
   *
   * @param credentials Objeto con el correo y la contraseña del usuario.
   * @param credentials.email Correo electrónico del usuario.
   * @param credentials.password Contraseña del usuario.
   * @returns Observable que emite las credenciales del usuario tras la reautenticación.
   * @throws Error si no existe un usuario autenticado en el contexto actual.
   */
  reauthenticateWithPassword(credentials: {
    email: string;
    password: string;
  }): Observable<UserCredential> {
    const credential: AuthCredential = EmailAuthProvider.credential(
      credentials.email,
      credentials.password
    );
    if (!this.auth.currentUser) throw new Error('Usuario no autenticado');
    return from(
      runInInjectionContext(this.injector, () =>
        reauthenticateWithCredential(this.auth.currentUser!, credential)
      )
    );
  }

  /**
   * Genera un secreto TOTP para el usuario actual y devuelve datos útiles para su configuración.
   *
   * @returns Observable que emite el URI para QR, la clave secreta y el objeto `TotpSecret`.
   * @throws Error si no existe un usuario autenticado en el contexto actual.
   */
  generateTotpSecret(): Observable<{
    totpUri: string;
    secretKey: string;
    totpSecret: TotpSecret;
  }> {
    if (!this.auth.currentUser) throw new Error('Usuario no autenticado');

    return from(
      runInInjectionContext(this.injector, async () => {
        const session: MultiFactorSession = await multiFactor(this.auth.currentUser!).getSession();
        const totpSecret: TotpSecret = await TotpMultiFactorGenerator.generateSecret(
          session
        );

        const accountName: string = this.auth.currentUser?.email || 'user';
        const issuer: string = 'Excuela';
        const totpUri: string = totpSecret.generateQrCodeUrl(accountName, issuer);

        return { totpUri, secretKey: totpSecret.secretKey, totpSecret };
      })
    );
  }

  /**
   * Inscribe el factor TOTP para el usuario actual utilizando el código de verificación.
   *
   * @param totpSecret El secreto TOTP generado previamente para el usuario.
   * @param verificationCode Código TOTP de verificación (normalmente de la app autenticadora).
   * @returns Observable que completa cuando la inscripción TOTP finaliza.
   * @throws Error si no existe un usuario autenticado en el contexto actual.
   */
  saveTotp(totpSecret: TotpSecret, verificationCode: string): Observable<void> {
    if (!this.auth.currentUser) throw new Error('Usuario no autenticado');
    const assertion: MultiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
      totpSecret,
      verificationCode.toString()
    );
    return from(
      runInInjectionContext(this.injector, () =>
        multiFactor(this.auth.currentUser!).enroll(assertion, 'TOTP')
      )
    );
  }

  /**
   * Verifica el código TOTP en un flujo de inicio de sesión con MFA pendiente utilizando el `mfaResolver`.
   *
   * @param verificationCode Código TOTP introducido por el usuario.
   * @returns Observable que emite las credenciales del usuario cuando el `resolveSignIn` finaliza.
   * @throws Error si no hay un `mfaResolver` activo.
   */
  verifyTotp(verificationCode: string): Observable<UserCredential> {
    if (!this.mfaResolver) {
      this.notificationService.show(
        'Error',
        'Su sesión de verificación ha expirado. Por favor, inicie sesión nuevamente.',
        'error'
      );
      this.router.navigate(['/login']);
      return EMPTY;
    }

    const hint: MultiFactorInfo =
      this.mfaResolver.hints.find(
        (h: MultiFactorInfo) =>
          h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      ) || this.mfaResolver.hints[0];
    const assertion: MultiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
      hint.uid,
      verificationCode.toString()
    );
    return from(
      runInInjectionContext(this.injector, () =>
        this.mfaResolver!.resolveSignIn(assertion)
      )
    );
  }

  /**
   * Desactiva 2FA (TOTP) en un solo flujo:
   * - Si hay un reto MFA pendiente, lo resuelve con el código proporcionado
   * - Luego desinscribe (unenroll) el factor TOTP del usuario actual
   */
  disableTwoFactorAuth(verificationCode: string): Observable<void> {
    if (!this.auth.currentUser) throw new Error('Usuario no autenticado');

    this.mfaResolver;
    from(
      runInInjectionContext(this.injector, async () => {
        const hint: MultiFactorInfo =
          this.mfaResolver!.hints.find(
            (h: MultiFactorInfo) =>
              h.factorId === TotpMultiFactorGenerator.FACTOR_ID
          ) || this.mfaResolver!.hints[0];
        const assertion: MultiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
          hint.uid,
          verificationCode.toString()
        );
        await this.mfaResolver!.resolveSignIn(assertion);
      })
    );

    return from(
      runInInjectionContext(this.injector, async () => {
        const mfaUser: MultiFactorUser = multiFactor(this.auth.currentUser!);
        const enrolledTotp: MultiFactorInfo =
          mfaUser.enrolledFactors.find(
            (h: MultiFactorInfo) =>
              h.factorId === TotpMultiFactorGenerator.FACTOR_ID
          ) || mfaUser.enrolledFactors[0];
        if (!enrolledTotp) return;
        await mfaUser.unenroll(enrolledTotp.uid);
        this.mfaResolver = null;
      })
    );
  }

  /**
   * Configura el `MultiFactorResolver` a utilizar durante el flujo de verificación MFA.
   *
   * @param resolver Instancia de `MultiFactorResolver` proporcionada por Firebase al detectar un desafío MFA.
   */
  setResolver(error: MultiFactorError): void {
    runInInjectionContext(this.injector, () => {
      const resolver: MultiFactorResolver = getMultiFactorResolver(this.auth, error);
      this.mfaResolver = resolver;
    });
  }
}